import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { HttpBaseService } from 'src/app/base/http-base.service';
import RoleInterimActions from '../../actions/generations/role.actions';
import { catchError, EMPTY, finalize, map, of, switchMap, tap } from 'rxjs';
import { AppLoaderService } from 'src/app/services/app-loader.service';
import { TranslateService } from '@ngx-translate/core';
import { UtilService } from 'src/app/services/util.service';

@Injectable()
export class RoleInterimEffects {
  private readonly actions = inject(Actions);
  private readonly httpService = inject(HttpBaseService);
  private readonly loaderService = inject(AppLoaderService);
  private readonly utilService = inject(UtilService);
  private readonly translateService = inject(TranslateService);

  getAllRoles$ = createEffect(() =>
    this.actions.pipe(
      ofType(RoleInterimActions.getAllRoles),
      tap(() => {
        this.loaderService.show();
      }),
      switchMap((action: any) => {
        const { apiConstants, payloadWithPathVariables } = action;
        return this.httpService[apiConstants.method](apiConstants, payloadWithPathVariables).pipe(
          map((response: any) => {
            const roles = response?.results || [];
            return RoleInterimActions.setRoles({ roles });
          }),
          catchError((error) => {
            console.error('Error in getAllRoles API call:', error);
            return of(RoleInterimActions.setRoles({ roles: [] }));
          }),
          finalize(() => {
            this.loaderService.hide();
          })
        );
      })
    )
  );

  createRole$ = createEffect(() =>
    this.actions.pipe(
      ofType(RoleInterimActions.createRole),
      tap(() => {
        this.loaderService.show();
      }),
      switchMap((action: any) => {
        const { apiConstants, urlPathVariables } = action;
        return this.httpService[apiConstants.method](apiConstants, urlPathVariables).pipe(
          map((response: any) => {
            return RoleInterimActions.createRoleSuccess({ role: response });
          }),
          catchError((error) => {
            console.error('Error in createNewRoleIntrim API call:', error);
            return of(EMPTY);
          }),
          finalize(() => {
            this.loaderService.hide();
          })
        );
      })
    )
  );

  createRoleSuccess$ = createEffect(
    () =>
      this.actions.pipe(
        ofType(RoleInterimActions.createRoleSuccess),
        tap((action: any) => {
          this.utilService.showSuccess(
            this.translateService.instant('COMPONENT_ADDED_SUCCESSFULLY', { component: 'Role' }) as string
          );
        })
      ),
    { dispatch: false }
  );

  updateRole$ = createEffect(() =>
    this.actions.pipe(
      ofType(RoleInterimActions.updateRole),
      tap(() => {
        this.loaderService.show();
      }),
      switchMap((action: any) => {
        const { apiConstants, urlPathVariables } = action;
        return this.httpService[apiConstants.method](apiConstants, urlPathVariables).pipe(
          map((response: any) => {
            return RoleInterimActions.updateRoleSuccess({ role: response });
          }),
          catchError((error) => {
            console.error('Error in updateRole API call:', error);
            return EMPTY;
          }),
          finalize(() => {
            this.loaderService.hide();
          })
        );
      })
    )
  );

  updateRoleSuccess$ = createEffect(
    () =>
      this.actions.pipe(
        ofType(RoleInterimActions.updateRoleSuccess),
        tap((action: any) => {
          this.utilService.showSuccess(
            this.translateService.instant('COMPONENT_UPDATED_SUCCESSFULLY', { component: 'Role' }) as string
          );
        })
      ),
    { dispatch: false }
  );

  updateRoleFailure$ = createEffect(
    () =>
      this.actions.pipe(
        ofType(RoleInterimActions.updateRoleFailure),
        map(() => {
          return EMPTY; // No further action to dispatch
        })
      ),
    { dispatch: false }
  );

  deleteRole$ = createEffect(() =>
    this.actions.pipe(
      ofType(RoleInterimActions.deleteRole),
      tap(() => {
        this.loaderService.show();
      }),
      switchMap((action: any) => {
        const { apiConstants, urlPathVariables } = action;
        return this.httpService[apiConstants.method](apiConstants, urlPathVariables).pipe(
          map(() => {
            return RoleInterimActions.deleteRoleSuccess({ roleId: urlPathVariables.roleId });
          }),
          catchError((error) => {
            console.error('Error in deleteRole API call:', error);
            return EMPTY;
          }),
          finalize(() => {
            this.loaderService.hide();
          })
        );
      })
    )
  );

  deleteRoleSuccess$ = createEffect(
    () =>
      this.actions.pipe(
        ofType(RoleInterimActions.deleteRoleSuccess),
        map(({ roleId: _roleId }) => {
          this.utilService.showSuccess(
            this.translateService.instant('COMPONENT_DELETED_SUCCESSFULLY', { component: 'Role' }) as string
          );
          return EMPTY; // No further action to dispatch
        })
      ),
    { dispatch: false }
  );

  deleteRoleFailure$ = createEffect(
    () =>
      this.actions.pipe(
        ofType(RoleInterimActions.deleteRoleFailure),
        map(() => {
          return EMPTY; // No further action to dispatch
        })
      ),
    { dispatch: false }
  );
}
